import validate from 'validate.js/validate';

export interface IUniqueByConstraint {
  /**
   * The object keys that together identify a row. Every key has to match for two rows to
   * be duplicates. Omit for an array of scalars, which are compared directly.
   */
  keys?: string[];
  /** Compare case-sensitively. Defaults to false, so `Donate` and `donate` collide. */
  caseSensitive?: boolean;
  /**
   * Message for a repeated row. `%{value}` is the first key's value as originally
   * entered, and `%{number}` is the 1-based position of the row it repeats.
   */
  message?: string;
}

/**
 * Flags the rows of an array that repeat an earlier one, index-aligned so the offending
 * row can be marked in place.
 *
 * Prefer this to `duplicates` for arrays of objects or when case should be ignored:
 * `duplicates` compares object rows by reference unless given `properties`, treats those
 * properties as OR rather than AND, has a `caseSensitive` flag that does the opposite of
 * its name, and throws on an object array when that flag is set. It also reports only a
 * single message for the whole attribute, which cannot be shown against a row.
 *
 * The first occurrence is left clean and each later one is flagged, so the message lands
 * on the row the user just typed rather than the one they wrote earlier. Rows with an
 * empty identity are placeholders rather than duplicates, and are skipped.
 *
 * @param value - The array to check. A non-array is ignored.
 * @param options - {@link IUniqueByConstraint}.
 * @returns One entry per row — a message for a repeat, `undefined` otherwise — or
 * `undefined` when nothing repeats.
 */
function uniqueBy(
  value: unknown,
  options: IUniqueByConstraint,
): Array<string | undefined> | undefined {
  if (!Array.isArray(value)) {
    return undefined;
  }

  const { keys, caseSensitive = false, message = '^This is a duplicate.' } = options;

  function getParts(row: unknown): string[] {
    const values = keys?.length
      ? keys.map((key) => (row as Record<string, unknown>)?.[key])
      : [row];
    return values.map((part) => (part === undefined || part === null ? '' : String(part).trim()));
  }

  /** Identity of the first row seen, mapped to its position and its displayable value. */
  const firstSeen = new Map<string, { number: number; value: string }>();

  const errors = value.map((row, index) => {
    const parts = getParts(row);
    if (parts.every((part) => !part)) {
      return undefined;
    }

    // NUL separator: a printable one could be typed into a value and forge a collision,
    // e.g. keys ['a', 'b'] matching ['x|', 'y'] against ['x', '|y'].
    const joined = parts.join('\0');
    const identity = caseSensitive ? joined : joined.toLowerCase();
    const original = firstSeen.get(identity);
    if (!original) {
      firstSeen.set(identity, { number: index + 1, value: parts[0] });
      return undefined;
    }

    return validate.format(message, { number: original.number, value: original.value });
  });

  return errors.some(Boolean) ? errors : undefined;
}

export default uniqueBy;
